import { escapeHtml } from '@colplat/ui-web';
import type { AccountHistory, WalletHistory } from '@colplat/app-webledger/domain';

/**
 * A wallet's balance over time, one small chart per currency.
 *
 * Small multiples rather than one chart with several lines, and not a style
 * choice: a wallet here holds 1,781 CAD and −7,836,470 HUF, and one y-axis
 * across both would draw the forint and a flat line labelled "everything
 * else". Each currency gets its own y-scale and its own chart, beside the
 * chip that names it — so identity is carried by the chip and the chart needs
 * no legend and no series colour at all.
 *
 * The x-axis is SHARED: every chart on the page maps the same span of days
 * onto the same pixels, which is what makes scrolling them together mean
 * something. Scroll one and they all move, so a reader comparing two wallets
 * is always comparing the same fortnight.
 */

/** Days per pixel is the wrong way round: this is pixels per day. */
const PX_PER_DAY = 1.6;
const MIN_WIDTH = 220;
export const SPARK_HEIGHT = 30;

function dayNumber(iso: string): number {
  return Math.floor(Date.parse(`${iso}T00:00:00Z`) / 86_400_000);
}

export interface SparkSpan {
  from: number;
  to: number;
  width: number;
}

/**
 * The span every chart on the page is drawn against.
 *
 * Taken from all of them together, so a wallet that only woke up in August
 * still shows August in the same place as a wallet that has been running
 * since January — which is the entire point of a shared axis.
 */
export function sparkSpan(all: readonly (readonly { points: readonly { date: string }[] }[])[]): SparkSpan | undefined {
  let from = Infinity;
  let to = -Infinity;
  for (const wallet of all) {
    for (const series of wallet) {
      for (const point of series.points) {
        const day = dayNumber(point.date);
        if (day < from) from = day;
        if (day > to) to = day;
      }
    }
  }
  if (!Number.isFinite(from) || !Number.isFinite(to)) return undefined;
  return { from, to, width: Math.max(MIN_WIDTH, Math.round((to - from + 1) * PX_PER_DAY)) };
}

/**
 * One chart, as inline SVG.
 *
 * `preserveAspectRatio="none"` with a viewBox in pixel units, so the path is
 * written once in the span's own coordinates and the element does not have to
 * be measured before it can be drawn.
 *
 * The zero line is drawn whenever the series crosses it, and the part of the
 * area below it is tinted — position carries "this went under" and the colour
 * only agrees, which is the whole of invariant #11's requirement here.
 */
let clipSeq = 0;

export function sparklineSvg(series: WalletHistory, span: SparkSpan, markDay?: string): string {
  const pts = series.points;
  if (pts.length === 0) return '';
  /*
   * A fresh id per chart, because ids are global to the document.
   *
   * Every chart used to declare the same `wlSparkUnder`, so all fourteen of
   * them resolved to whichever one the parser saw first and were clipped to
   * ITS zero line. It was invisible while the clip only tinted an area;
   * splitting the line itself on the same boundary would have made it
   * obvious, and wrong.
   */
  const uid = `wlSpark${clipSeq += 1}`;

  const values = pts.map((p) => Number(p.value));
  const lo = Math.min(0, ...values);
  const hi = Math.max(0, ...values);
  const range = hi - lo || 1;
  const pad = 3;
  const plot = SPARK_HEIGHT - pad * 2;

  const x = (iso: string): number =>
    (span.to === span.from ? span.width / 2
      : ((dayNumber(iso) - span.from) / (span.to - span.from)) * span.width);
  const y = (v: number): number => pad + (hi - v) / range * plot;

  // A balance holds its value until something moves it, so the line between
  // two entries is flat and then steps — drawing it as a diagonal would claim
  // the money drained away over three weeks when it left on a Tuesday.
  const steps: string[] = [];
  pts.forEach((p, i) => {
    const px = x(p.date);
    const py = y(Number(p.value));
    steps.push(i === 0 ? `M${px.toFixed(1)},${py.toFixed(1)}` : `H${px.toFixed(1)}`);
    steps.push(`V${py.toFixed(1)}`);
  });
  // Carried to the right-hand edge: the balance is still that until today.
  steps.push(`H${span.width.toFixed(1)}`);

  const zero = y(0);
  const underZero = lo < 0;
  const path = steps.join(' ');

  /*
   * The line is drawn TWICE, clipped above and below the zero line, so it is
   * green while the wallet is in funds and red while it is not — and changes
   * colour exactly where it crosses rather than per segment.
   *
   * Colour is not carrying this on its own: the same fact is the line's
   * position relative to a marked zero, and the figure beside it has a minus
   * sign (invariant #11). What the colour buys is reading a whole column of
   * charts at a glance, which is what they are for.
   */
  const line = (clip: string | undefined, stroke: string): string =>
    `<path d="${path}" fill="none" stroke="${stroke}" stroke-width="2"
      stroke-linejoin="round" vector-effect="non-scaling-stroke"
      ${clip ? `clip-path="url(#${clip})"` : ''}/>`;

  const clips = underZero
    ? `<clipPath id="${uid}over"><rect x="0" y="0" width="${span.width}" height="${zero.toFixed(1)}"/></clipPath>`
      + `<clipPath id="${uid}under"><rect x="0" y="${zero.toFixed(1)}" width="${span.width}" height="${(SPARK_HEIGHT - zero).toFixed(1)}"/></clipPath>`
    : '';

  return `<svg class="wl-spark__svg" width="${span.width}" height="${SPARK_HEIGHT}"
      viewBox="0 0 ${span.width} ${SPARK_HEIGHT}" preserveAspectRatio="none"
      role="img" aria-label="${escapeHtml(series.currencyCode)} balance over time" focusable="false">
    <defs>${clips}</defs>
    ${underZero ? `<path d="${path} V${zero.toFixed(1)} H0 Z" fill="var(--wl-spark-under)" clip-path="url(#${uid}under)"/>` : ''}
    ${underZero ? `<line x1="0" y1="${zero.toFixed(1)}" x2="${span.width}" y2="${zero.toFixed(1)}"
      stroke="var(--wl-spark-zero)" stroke-width="1" stroke-dasharray="2 2"/>` : ''}
    ${underZero ? line(`${uid}over`, 'var(--wl-spark-up)') + line(`${uid}under`, 'var(--wl-spark-down)')
      : line(undefined, 'var(--wl-spark-up)')}
    ${markDay ? `<line class="wl-spark__mark" x1="${dayX(span, markDay).toFixed(1)}" y1="0" x2="${dayX(span, markDay).toFixed(1)}"
      y2="${SPARK_HEIGHT}" stroke="var(--wl-spark-mark)" stroke-width="2" vector-effect="non-scaling-stroke"/>` : ''}
  </svg>`;
}

/** Everything one chart needs to answer a hover, kept beside the element. */
export interface SparkLookup {
  span: SparkSpan;
  series: WalletHistory;
}

function isoOfDay(day: number): string {
  return new Date(day * 86_400_000).toISOString().slice(0, 10);
}

export interface SparkReading {
  /** The day the POINTER is over — not the day the balance last moved. */
  date: string;
  /** What the balance was on that day: the last movement at or before it. */
  value: string;
  /** Whether anything had happened by then at all. */
  known: boolean;
}

/**
 * What the chart says about the day under the pointer.
 *
 * The date is the pointer's, and that is the correction: naming the last
 * entry's date instead made the tooltip sit still across a three-week flat
 * stretch and jump at the steps, which reads as a broken readout rather than
 * as a balance that did not change. A balance is a fact about every day,
 * including the quiet ones — so the date moves continuously and the figure
 * is whatever was in force then.
 */
export function pointAt(lookup: SparkLookup, offsetX: number): SparkReading | undefined {
  const { span, series } = lookup;
  if (series.points.length === 0) return undefined;
  const ratio = span.width === 0 ? 0 : Math.min(1, Math.max(0, offsetX / span.width));
  const day = Math.round(span.from + ratio * (span.to - span.from));

  let value: string | undefined;
  for (const p of series.points) {
    if (dayNumber(p.date) <= day) value = p.value; else break;
  }
  // Before the first entry the wallet held nothing, and saying so is better
  // than printing the earliest balance against a date it did not apply to.
  return { date: isoOfDay(day), value: value ?? '0', known: value !== undefined };
}

// ------------------------------------------------------------ stacked by wallet

/**
 * Where each part sits in a stack that grows both ways from zero.
 *
 * The parts of one account's balance can have opposite signs — a wallet that
 * has spent more of the Safe's forints than it put in is below zero inside an
 * account that is above it. Stacking those naively draws a band that starts
 * above the axis and ends below where it started. So the positive parts
 * stack upward from zero and the negative ones downward, each in the same
 * key order on every day, and the bands above and below the axis add up to
 * the two halves of the total.
 *
 * Returns `[low, high]` per key; a zero part is `[x, x]`, a band with no
 * height, which draws nothing and keeps its neighbours where they were.
 */
export function stackBands(parts: Readonly<Record<string, string>>, keys: readonly string[]): Map<string, [number, number]> {
  const out = new Map<string, [number, number]>();
  let up = 0;
  let down = 0;
  for (const k of keys) {
    const v = Number(parts[k] ?? 0);
    if (v >= 0) { out.set(k, [up, up + v]); up += v; } else { out.set(k, [down + v, down]); down += v; }
  }
  return out;
}

/**
 * One account's balance in one currency, as bands by wallet with the total
 * drawn over them.
 *
 * The same axis, the same step shape and the same zero line as the wallet
 * charts, so a reader who knows one reads the other. The difference is the
 * fill: where a wallet's chart has one series and needs no colour, this one
 * has several and the colour is what says whose each band is. It is never
 * the only thing that says so — the tooltip names every part, and the page
 * draws a legend beside the switch that shows this column (invariant #11).
 *
 * The total follows the wallet charts' rule (asked from use, 2026-10-21):
 * green above zero and red below, split exactly where it crosses, and drawn
 * over the bands. It was ink, on the reasoning that green and red could be
 * mistaken for wallet colours — but one rule for "is this in funds" across
 * every chart reads faster than a second rule that is only safer in theory.
 * The colour is still not alone: the line's position against the dashed
 * zero says the same (invariant #11).
 */
export function stackedSparkSvg(
  series: AccountHistory, span: SparkSpan, colourOf: (key: string) => string, markDay?: string,
): string {
  const pts = series.points;
  if (pts.length === 0) return '';
  const totals = pts.map((p) => Number(p.total));
  const stacks = pts.map((p) => stackBands(p.parts, series.keys));
  let lo = Math.min(0, ...totals);
  let hi = Math.max(0, ...totals);
  for (const stack of stacks) {
    for (const [a, b] of stack.values()) { lo = Math.min(lo, a); hi = Math.max(hi, b); }
  }
  const range = hi - lo || 1;
  const pad = 3;
  const plot = SPARK_HEIGHT - pad * 2;
  const x = (iso: string): number =>
    (span.to === span.from ? span.width / 2
      : ((dayNumber(iso) - span.from) / (span.to - span.from)) * span.width);
  const y = (v: number): number => pad + (hi - v) / range * plot;
  const f = (n: number): string => n.toFixed(1);

  /*
   * A band is a step shape along its top edge, left to right, and back
   * along its bottom edge, right to left. Each day's value holds until the
   * next day's x, and the last holds to the right-hand edge — the same rule
   * as the line: a balance is still that until something moves it.
   */
  const xs = pts.map((p) => x(p.date));
  const ends = xs.map((_, i) => (i + 1 < xs.length ? xs[i + 1]! : span.width));
  const bands = series.keys.map((key) => {
    let top = '';
    let bottom = '';
    let any = false;
    for (let i = 0; i < pts.length; i += 1) {
      const [a, b] = stacks[i]!.get(key)!;
      if (a !== b) any = true;
      top += `${i === 0 ? 'M' : 'L'}${f(xs[i]!)},${f(y(b))} L${f(ends[i]!)},${f(y(b))} `;
    }
    for (let i = pts.length - 1; i >= 0; i -= 1) {
      const [a] = stacks[i]!.get(key)!;
      bottom += `L${f(ends[i]!)},${f(y(a))} L${f(xs[i]!)},${f(y(a))} `;
    }
    return any ? `<path d="${top}${bottom}Z" fill="${escapeHtml(colourOf(key))}" fill-opacity=".72"/>` : '';
  }).join('');

  const line: string[] = [];
  pts.forEach((p, i) => {
    const py = f(y(Number(p.total)));
    line.push(i === 0 ? `M${f(xs[i]!)},${py}` : `H${f(xs[i]!)}`, `V${py}`);
  });
  line.push(`H${f(span.width)}`);
  const zero = y(0);
  const uid = `wlSpark${clipSeq += 1}`;
  const underZero = lo < 0;
  const path = line.join(' ');
  const total = (clip: string | undefined, stroke: string): string =>
    `<path d="${path}" fill="none" stroke="${stroke}" stroke-width="2"
      stroke-linejoin="round" vector-effect="non-scaling-stroke"
      ${clip ? `clip-path="url(#${clip})"` : ''}/>`;
  const clips = underZero
    ? `<defs><clipPath id="${uid}over"><rect x="0" y="0" width="${span.width}" height="${f(zero)}"/></clipPath>`
      + `<clipPath id="${uid}under"><rect x="0" y="${f(zero)}" width="${span.width}" height="${f(SPARK_HEIGHT - zero)}"/></clipPath></defs>`
    : '';
  const zeroLine = lo < 0
    ? `<line x1="0" y1="${f(zero)}" x2="${span.width}" y2="${f(zero)}"
      stroke="var(--wl-spark-zero)" stroke-width="1" stroke-dasharray="2 2"/>`
    : '';

  return `<svg class="wl-spark__svg" width="${span.width}" height="${SPARK_HEIGHT}"
      viewBox="0 0 ${span.width} ${SPARK_HEIGHT}" preserveAspectRatio="none"
      role="img" aria-label="${escapeHtml(series.currencyCode)} balance over time, by wallet" focusable="false">
    ${clips}${bands}${zeroLine}
    ${underZero ? total(`${uid}over`, 'var(--wl-spark-up)') + total(`${uid}under`, 'var(--wl-spark-down)')
      : total(undefined, 'var(--wl-spark-up)')}
    ${markDay ? `<line class="wl-spark__mark" x1="${f(dayX(span, markDay))}" y1="0" x2="${f(dayX(span, markDay))}" y2="${SPARK_HEIGHT}"
      stroke="var(--wl-spark-mark)" stroke-width="2" vector-effect="non-scaling-stroke"/>` : ''}
  </svg>`;
}

/** Where a day falls on the shared axis, in the span's pixels. */
export function dayX(span: SparkSpan, iso: string): number {
  return span.to === span.from ? span.width / 2 : ((dayNumber(iso) - span.from) / (span.to - span.from)) * span.width;
}

/** The day under the pointer, as an ISO date — the stacked chart's half of `pointAt`. */
export function dayAt(span: SparkSpan, offsetX: number): string {
  const ratio = span.width === 0 ? 0 : Math.min(1, Math.max(0, offsetX / span.width));
  return isoOfDay(Math.round(span.from + ratio * (span.to - span.from)));
}

/** What a series said at the end of `date`: its last point at or before it. */
export function accountPointAt(series: AccountHistory, date: string): AccountHistory['points'][number] | undefined {
  let found: AccountHistory['points'][number] | undefined;
  for (const p of series.points) {
    if (p.date <= date) found = p; else break;
  }
  return found;
}

// ------------------------------------------------- scrolling them all together

/** The one horizontal position every chart in a list shares. `-1` until first placed. */
export interface SparkScroll {
  position: number;
}

/**
 * Keep a draggable ancestor from being dragged by a press that began on a
 * chart, until the button comes up.
 *
 * The chart of accounts reorders its rows by native drag and drop, so every
 * row is `draggable` — and a native drag starts from a press ANYWHERE inside
 * one, chart included. Scrubbing a chart picked up the whole account row
 * instead. Reported from use. `dragstart` fires on the row, not on the
 * chart, so it cannot be refused from here; switching the row off for the
 * length of the press is what stops it starting.
 */
function holdStill(box: HTMLElement, released: () => void): void {
  const row = box.closest<HTMLElement>('[draggable="true"]');
  row?.setAttribute('draggable', 'false');
  const restore = (): void => {
    row?.setAttribute('draggable', 'true');
    released();
    document.removeEventListener('pointerup', restore, true);
    document.removeEventListener('pointercancel', cancelled, true);
  };
  // A browser starting a native drag cancels the pointer, and may do it just
  // BEFORE `dragstart` — so a cancel lets go a tick later, after the drag it
  // announced has been refused, rather than clearing the flag that refuses it.
  const cancelled = (): void => { setTimeout(restore, 0); };
  // On the document, because the press may end anywhere — and a chart that
  // cannot scroll never captures the pointer.
  document.addEventListener('pointerup', restore, true);
  document.addEventListener('pointercancel', cancelled, true);
}

/** Whether the click now arriving in `container` is the end of a drag, and so not a click. */
export function justDragged(container: HTMLElement): boolean {
  return gestures.get(container)?.dragged ?? false;
}

/**
 * Scroll every chart in a list so `day` is on screen, a little in from the
 * edge — what makes stepping off one side bring the history in under the
 * line. Sets the shared position the next `wireSparkScroll` applies.
 */
export function scrollToDay(box: HTMLElement | null, span: SparkSpan, day: string, state: SparkScroll): void {
  if (!box) return;
  const x = dayX(span, day);
  const width = box.clientWidth;
  const max = Math.max(0, box.scrollWidth - width);
  const margin = Math.min(40, width / 4);
  const at = state.position < 0 ? max : state.position;
  if (x < at + margin) state.position = Math.max(0, x - margin);
  else if (x > at + width - margin) state.position = Math.min(max, x - width + margin);
  else state.position = at;
}

/** Per list, because a drag that ended as a click must be swallowed in that list only. */
const gestures = new WeakMap<HTMLElement, { dragged: boolean; dragging: boolean; pressed: boolean }>();

/**
 * Scroll every chart in `container` as one, by drag (or a finger, natively).
 *
 * Moved here from the wallet page so the accounts page scrolls the same way,
 * rather than a second copy that would drift from the first the next time
 * either was touched. What it does, and why, is unchanged:
 *
 * - The scrollbars are hidden on each chart and the position is held in
 *   `state` instead: a scrollbar per row is noise, and they would disagree
 *   the moment one was dragged. Scroll one and they all move, which is the
 *   only behaviour that keeps a shared axis honest.
 * - It opens at the RIGHT-hand end: the axis runs from the ledger's first
 *   entry, and what a reader of a balance wants is where it stands now.
 * - An inset shadow on whichever edge has more behind it is the only thing
 *   saying there is somewhere to scroll to.
 * - No wheel. It used to turn a vertical wheel into a sideways scroll and
 *   fall through to the page at either end — so scrolling a chart to its
 *   end started scrolling the page under the pointer. Reported from use. A
 *   wheel over a chart now scrolls the page, as it does everywhere else;
 *   the charts move by dragging (and on the accounts page by stepping).
 * - Mouse and pen drag to scrub. Touch is left to the browser, which already
 *   pans a scrolling box with momentum this would have to reimplement.
 * - A drag that travelled is not also a click: the charts sit inside things
 *   that open on click, and every scrub used to end by navigating away.
 *
 * Returns whether a drag is in progress, for a tooltip that should keep out
 * of the way while one is.
 */
export function wireSparkScroll(
  container: HTMLElement, boxes: readonly HTMLElement[], state: SparkScroll,
): { isDragging(): boolean } {
  let gesture = gestures.get(container);
  if (!gesture) {
    gesture = { dragged: false, dragging: false, pressed: false };
    gestures.set(container, gesture);
    const g = gesture;
    /*
     * And no native drag may start from a press that began on a chart.
     *
     * `holdStill` switches the row's `draggable` off for the press, which is
     * enough where the browser decides what to drag when the pointer starts
     * moving — and was reported as not enough in practice. Cancelling
     * `dragstart` is the one thing every browser honours, whenever it
     * decided. Capture phase on the container, so it is ahead of the row's
     * own handler.
     */
    container.addEventListener('dragstart', (event) => {
      if (!g.pressed) return;
      event.preventDefault();
      event.stopPropagation();
    }, true);
    // Capture phase, so this is ahead of whatever the click would open.
    container.addEventListener('click', (event) => {
      if (!g.dragged) return;
      g.dragged = false;
      event.stopPropagation();
      event.preventDefault();
    }, true);
  }
  const g = gesture;

  if (state.position < 0) {
    const widest = boxes.find((box) => box.scrollWidth > box.clientWidth);
    state.position = widest ? widest.scrollWidth - widest.clientWidth : 0;
  }
  for (const box of boxes) box.scrollLeft = state.position;

  const paintEdges = (): void => {
    for (const box of boxes) {
      const max = box.scrollWidth - box.clientWidth;
      box.classList.toggle('is-more-left', box.scrollLeft > 1);
      box.classList.toggle('is-more-right', box.scrollLeft < max - 1);
    }
  };

  let dragFrom: { x: number; scroll: number; box: HTMLElement } | undefined;
  let syncing = false;
  for (const box of boxes) {
    box.addEventListener('scroll', () => {
      if (syncing) return;
      syncing = true;
      state.position = box.scrollLeft;
      for (const other of boxes) if (other !== box) other.scrollLeft = state.position;
      paintEdges();
      // Released on the next frame: setting `scrollLeft` fires `scroll` on
      // each of the others, and without the latch each would set this back.
      requestAnimationFrame(() => { syncing = false; });
    });
    box.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch' || event.button !== 0) return;
      g.pressed = true;
      holdStill(box, () => { g.pressed = false; });
      if (box.scrollWidth - box.clientWidth <= 0) return;
      dragFrom = { x: event.clientX, scroll: box.scrollLeft, box };
      g.dragged = false;
      g.dragging = true;
      box.classList.add('is-dragging');
      // Keeps the drag alive when the pointer leaves a thirty-pixel track.
      try { box.setPointerCapture(event.pointerId); } catch { /* not capturable */ }
    });
    box.addEventListener('pointermove', (event) => {
      if (!dragFrom || dragFrom.box !== box) return;
      const moved = event.clientX - dragFrom.x;
      // A few pixels of slop, so an unsteady click is still a click.
      if (Math.abs(moved) > 3) g.dragged = true;
      box.scrollLeft = dragFrom.scroll - moved;
    });
    const endDrag = (event: PointerEvent): void => {
      if (!dragFrom || dragFrom.box !== box) return;
      dragFrom = undefined;
      g.dragging = false;
      box.classList.remove('is-dragging');
      if (box.hasPointerCapture(event.pointerId)) box.releasePointerCapture(event.pointerId);
    };
    box.addEventListener('pointerup', endDrag);
    box.addEventListener('pointercancel', endDrag);
  }
  paintEdges();
  return { isDragging: () => g.dragging };
}
